import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock, Package } from "lucide-react";
import { api, formatApiError, imgSrc, inr } from "@/lib/api";

const STATUS_LABELS = {
  new: "Order received",
  confirmed: "Confirmed by studio",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(undefined);
  const [payConfig, setPayConfig] = useState(null);

  const load = () =>
    api
      .get(`/orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null));

  useEffect(() => {
    load();
    api.get("/payments/config").then((r) => setPayConfig(r.data)).catch(() => setPayConfig({ enabled: false }));
  }, [id]);

  const retryPayment = async () => {
    try {
      const { data: payOrder } = await api.post("/payments/create-order", { amount: order.total });
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = resolve;
          s.onerror = reject;
          document.body.appendChild(s);
        });
      }
      window.Razorpay({
        key: payOrder.key_id,
        amount: payOrder.amount,
        currency: payOrder.currency || "INR",
        order_id: payOrder.razorpay_order_id,
        name: "Knot & Cast",
        description: `Order ${order.order_number}`,
        prefill: { name: order.customer.name, email: order.customer.email, contact: order.customer.phone },
        theme: { color: "#C86A51" },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", { order_id: order.id, ...res });
            toast.success("Payment received — thank you!");
          } catch (e) {
            toast.error(formatApiError(e));
          }
          load();
        },
      }).open();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (order === undefined) {
    return (
      <div className="grid min-h-[70vh] place-items-center pt-24">
        <p className="label-mono">Finding your order…</p>
      </div>
    );
  }

  if (order === null) {
    return (
      <div className="grid min-h-[70vh] place-items-center px-6 pt-24 text-center" data-testid="order-not-found">
        <div>
          <p className="font-display text-4xl">We could not find that order.</p>
          <Link to="/shop" className="btn-solid mt-8" data-testid="order-not-found-shop-btn">
            Back to the collection
          </Link>
        </div>
      </div>
    );
  }

  const paid = order.payment_status === "paid";
  const isEnquiry = order.payment_method === "enquiry";

  return (
    <div className="pt-24 md:pt-32" data-testid="order-page">
      <div className="mx-auto max-w-3xl px-5 pb-24 md:px-8">
        <div className="text-center">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full border border-line bg-white text-terracotta">
            {paid ? <CheckCircle2 size={28} strokeWidth={1.5} /> : <Clock size={28} strokeWidth={1.5} />}
          </span>
          <h1 className="mt-6 font-display text-5xl tracking-tight" data-testid="order-thanks">
            {paid ? "Thank you — it is official." : "Order received."}
          </h1>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-stone2" data-testid="order-number">
            Order {order.order_number}
          </p>
        </div>

        <div className="mt-12 border border-line bg-white p-8" data-testid="order-summary-card">
          <div className="flex flex-wrap items-center gap-3" data-testid="order-badges">
            <span className="border border-line bg-cream px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em]">
              {STATUS_LABELS[order.order_status] || order.order_status}
            </span>
            <span className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${paid ? "bg-moss text-white" : "bg-ink text-parchment"}`}>
              {paid ? "Paid" : isEnquiry ? "Payment on confirmation" : "Payment pending"}
            </span>
          </div>

          <p className="mt-6 text-sm leading-relaxed text-stone2" data-testid="order-next-steps">
            {paid
              ? "Payment received — your order is confirmed. We will email you tracking as soon as it ships from the studio."
              : isEnquiry
              ? "Thank you! The studio will call or message you within 24 hours to confirm availability, payment and delivery."
              : "Your order is saved. Complete the payment to confirm it, or the studio will reach out to help."}
          </p>

          {!paid && !isEnquiry && payConfig?.enabled && (
            <button onClick={retryPayment} className="btn-solid mt-6" data-testid="retry-payment-btn">
              Retry payment
            </button>
          )}

          <div className="mt-8 space-y-4 border-t border-line pt-6" data-testid="order-items">
            {order.items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-4">
                <img src={imgSrc(i.image)} alt={i.name} className="h-14 w-14 border border-line object-cover" />
                <div className="flex-1">
                  <p className="font-display text-lg leading-tight">{i.name}</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-stone2">Qty {i.qty}</p>
                </div>
                <p className="text-sm font-semibold">{inr(i.price * i.qty)}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 space-y-1.5 border-t border-line pt-5 text-sm">
            <div className="flex justify-between"><span className="text-stone2">Subtotal</span><span>{inr(order.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-stone2">Shipping</span><span>{order.shipping === 0 ? "Free" : inr(order.shipping)}</span></div>
            <div className="flex justify-between border-t border-line pt-3 font-display text-2xl"><span>Total</span><span data-testid="order-total">{inr(order.total)}</span></div>
          </div>

          <div className="mt-8 border-t border-line pt-6">
            <p className="label-mono">Delivering to</p>
            <p className="mt-3 text-sm leading-relaxed text-stone2" data-testid="order-customer">
              {order.customer.name} · {order.customer.phone} · {order.customer.email}
              <br />
              {order.customer.address}, {order.customer.city} — {order.customer.pincode}
            </p>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link to="/shop" className="btn-outline" data-testid="order-continue-shopping">
            <Package size={16} strokeWidth={1.5} /> Keep browsing
          </Link>
        </div>
      </div>
    </div>
  );
}
